/**
 * The API client.
 *
 * Same-origin in development (through the Vite proxy) and in production (through nginx), so
 * the session cookie is always first-party and no token is ever kept in JavaScript.
 */

export interface ApiErrorDetail {
  path: string;
  message: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: ApiErrorDetail[];

  constructor(status: number, code: string, message: string, details: ApiErrorDetail[] = []) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  get isAuth(): boolean {
    return this.status === 401;
  }
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

async function request<T>(method: Method, path: string, body?: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      method,
      credentials: 'include',
      headers: body === undefined ? undefined : { 'content-type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, 'network', 'We could not reach Altar.Camp. Check your connection.');
  }

  if (response.status === 204) return undefined as T;

  const isJson = response.headers.get('content-type')?.includes('application/json');
  const payload = isJson ? await response.json().catch(() => null) : null;

  if (!response.ok) {
    const error = (
      payload as { error?: { code: string; message: string; details?: ApiErrorDetail[] } }
    )?.error;
    throw new ApiError(
      response.status,
      error?.code ?? 'error',
      // A 413 comes from the proxy in front of the API, as HTML rather than our JSON error.
      error?.message ??
        (response.status === 413 ? 'That file is too large to upload.' : 'Something went wrong.'),
      error?.details ?? [],
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string, body?: unknown) => request<T>('DELETE', path, body),
};

/** Downloads go through an authorised route, so they are fetched rather than linked. */
export async function downloadDocument(documentId: string, filename: string): Promise<void> {
  const response = await fetch(`/api/documents/${documentId}/content`, { credentials: 'include' });
  if (!response.ok)
    throw new ApiError(response.status, 'download_failed', 'That file could not be downloaded.');
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
